import ExpandMore from "@mui/icons-material/ExpandMore"
import SortRoundedIcon from "@mui/icons-material/SortRounded"
import { TextField, useMediaQuery, useTheme } from "@mui/material"
import MenuItem from "@mui/material/MenuItem"
import { ChangeEvent, CSSProperties, useRef, useState } from "react"

import { getColorVariables } from "src/components/layouts/basic/BasicLayout.variables"
import { create } from "src/helpers/bem"

import styles from "./FilterSortSelector.module.scss"

const bem = create(styles, "FilterSortSelector")
const style = { ...getColorVariables(undefined) }

interface Option {
  id: string
  label: string
}
interface FilterSortSelectorProps {
  onChange: (value: string) => void
  options: Option[]
  value: string
  disabled?: boolean
  className?: string
}

export const FilterSortSelection = ({
  onChange,
  options,
  value,
  disabled = false,
  className,
}: FilterSortSelectorProps) => {
  const [expanded, setExpanded] = useState<boolean>(false)
  const filterRef = useRef<HTMLInputElement>(null)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"))
  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    onChange(event.target.value)
  }

  const selected: Option | undefined = options
    ? options.find((selected) => selected.id === value)
    : undefined

  return (
    <TextField
      ref={filterRef}
      select
      className={`${className ?? ""} ${bem()}`}
      defaultValue={value}
      disabled={disabled}
      variant="standard"
      InputProps={{
        inputProps: { className: bem("input") },
        disableUnderline: true,
      }}
      SelectProps={{
        renderValue: () => <>{!isMobile && selected?.label}</>,
        onOpen: () => {
          setExpanded(true)
        },
        onClose: () => setExpanded(false),
        IconComponent: isMobile ? SortRoundedIcon : ExpandMore,
        MenuProps: {
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "right",
          },
          transformOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          slotProps: {
            paper: {
              className: bem("wrapper"),
              sx: {
                width: filterRef.current?.clientWidth,
                ...(isMobile && {
                  borderTop: "2px solid var(--color-primary)",
                  mt: "-2px",
                  minWidth: "200px",
                }),
              },
            },
          },
          MenuListProps: {
            className: bem("menu"),
            style: style as CSSProperties,
          },
          disableScrollLock: true,
        },
        className: bem("select", expanded ? "expanded" : ""),
      }}
      sx={{
        "& .MuiSelect-select.MuiInput-input": {
          minWidth: 0,
          paddingRight: "24px",
        },
        "& .MuiSelect-icon": {
          pointerEvents: "none",
          right: 8,
        },
      }}
      onChange={handleChange}
    >
      {options
        .filter((item) => item.id !== selected?.id)
        .map((option) => {
          return (
            <MenuItem
              key={option.id}
              className={bem("menuitem")}
              value={option.id}
            >
              {option.label}
            </MenuItem>
          )
        })}
    </TextField>
  )
}
